import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Mail, Lock, Eye, EyeOff, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/SupabaseAuthContext';
import { OAuthButtons } from './auth/OAuthButtons';
import { ForgotPasswordModal } from './auth/ForgotPasswordModal';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: 'login' | 'register';
}

export const AuthModal: React.FC<AuthModalProps> = ({ 
  isOpen, 
  onClose, 
  defaultMode = 'login' 
}) => {
  const [mode, setMode] = useState<'login' | 'register' | 'magic-link'>(defaultMode);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    fullName: '',
    userType: 'child' as 'child' | 'parent' | 'teacher' | 'creator'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  const { login, register, sendMagicLink, authError } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        onClose();
      } else if (mode === 'register') {
        await register!(formData);
        onClose();
      } else if (mode === 'magic-link') {
        await sendMagicLink!(formData.email);
        setSuccess('Magic link sent! Check your email and click the link to sign in.');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };



  const userTypes = [
    {
      id: 'child' as const,
      label: 'Young Creator',
      description: 'I want to learn and create cool projects',
      emoji: '🎨',
      color: 'from-pink-500 to-rose-500'
    },
    {
      id: 'parent' as const,
      label: 'Parent',
      description: 'I want to support my child\'s learning',
      emoji: '❤️',
      color: 'from-green-500 to-emerald-500'
    },
    {
      id: 'teacher' as const,
      label: 'Teacher',
      description: 'I want to use this in my classroom',
      emoji: '🎓',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      id: 'creator' as const,
      label: 'Content Creator',
      description: 'I want to create and sell templates',
      emoji: '⭐',
      color: 'from-purple-500 to-violet-500'
    }
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div key="auth-modal-backdrop" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-6 text-white relative">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-center space-x-3 mb-2">
              <Sparkles className="w-8 h-8" />
              <h2 className="text-2xl font-bold">
                {mode === 'login' ? 'Welcome Back!' : 'Join CraftyJoy'}
              </h2>
            </div>
            <p className="text-purple-100">
              {mode === 'login' 
                ? 'Sign in to continue your creative journey' 
                : 'Start your AI and coding adventure today'
              }
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Error Message */}
            {(error || authError) && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="text-red-500 text-lg">⚠️</div>
                  <div>
                    <h4 className="text-sm font-medium text-red-800 mb-1">
                      Authentication Error
                    </h4>
                    <p className="text-xs text-red-700">
                      {error || authError?.message}
                    </p>
                    {(error || authError?.type) === 'USER_NOT_FOUND' && mode === 'login' && (
                      <button
                        type="button"
                        onClick={() => setMode('register')}
                        className="mt-2 text-xs text-red-600 hover:text-red-800 underline"
                      >
                        Create an account instead
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="text-green-500 text-lg">✅</div>
                  <div>
                    <h4 className="text-sm font-medium text-green-800 mb-1">
                      Success!
                    </h4>
                    <p className="text-xs text-green-700">
                      {success}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {mode === 'register' && (
              <>
                {/* User Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    I am a...
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {userTypes.map((type) => (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => setFormData({ ...formData, userType: type.id })}
                        className={`p-3 rounded-xl border-2 transition-all text-left ${
                          formData.userType === type.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="text-lg mb-1">{type.emoji}</div>
                        <div className="text-sm font-medium text-gray-900">{type.label}</div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
              </>
            )}

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            {/* Password - only show for login and register modes */}
            {mode !== 'magic-link' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
            )}

            {/* Forgot Password Link */}
            {mode === 'login' && (
              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                >
                  Forgot your password?
                </button>
                <div className="text-gray-400 text-sm">or</div>
                <button
                  type="button"
                  onClick={() => setMode('magic-link')}
                  className="text-purple-600 hover:text-purple-700 text-sm font-medium"
                >
                  Sign in with magic link (no password needed)
                </button>
              </div>
            )}

            {/* Safety Notice for Children */}
            {mode === 'register' && formData.userType === 'child' && (
              <div className="bg-pink-50 border border-pink-200 rounded-xl p-4">
                <div className="flex items-start space-x-3">
                  <div className="text-pink-500 text-lg">🛡️</div>
                  <div>
                    <h4 className="text-sm font-medium text-pink-800 mb-1">
                      Safety First!
                    </h4>
                    <p className="text-xs text-pink-700">
                      Make sure to ask your parent or guardian for permission before creating an account. 
                      We keep you safe online!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || (mode === 'magic-link' && !!success)}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-4 rounded-xl font-semibold hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {loading ? 'Please wait...' : (
                mode === 'login' ? 'Sign In' : 
                mode === 'register' ? 'Create Account' : 
                'Send Magic Link'
              )}
            </button>

            {/* OAuth Buttons - only show for login and register modes, not magic-link */}
            {mode !== 'magic-link' && !success && (
              <OAuthButtons 
                loading={loading}
                onLoading={setLoading}
                onError={setError}
              />
            )}

            {/* Mode Switch */}
            <div className="text-center space-y-2">
              {mode === 'magic-link' && (
                <button
                  type="button"
                  onClick={() => setMode('login')}
                  className="text-purple-600 hover:text-purple-700 font-medium block w-full"
                >
                  Back to regular sign in
                </button>
              )}
              
              {mode !== 'magic-link' && (
                <button
                  type="button"
                  onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
                  className="text-purple-600 hover:text-purple-700 font-medium"
                >
                  {mode === 'login' 
                    ? "Don't have an account? Sign up" 
                    : 'Already have an account? Sign in'
                  }
                </button>
              )}
            </div>
          </form>
        </motion.div>
      </div>

      {/* Forgot Password Modal */}
      <ForgotPasswordModal
        key="forgot-password-modal"
        isOpen={showForgotPassword}
        onClose={() => setShowForgotPassword(false)}
        onBackToLogin={() => {
          setShowForgotPassword(false);
          setMode('login');
        }}
      />
    </AnimatePresence>
  );
};